import { firstValueFrom, take, timeout } from 'rxjs';
import { authService } from './auth.service';

const REFRESH_ID_TOKEN_TIMEOUT_MS = 15_000;

/**
 * Flags the two Sentry transports read to recognise an EXPECTED auth error.
 *
 *   - `__sessionExpired` is read by the GraphQL `responseHandler`, which catches the
 *     error and reports it via `captureMessage` (a Sentry *Message* event). This is the
 *     path that produced the WA-3514…WA-3538 ticket flood — a Message event has no
 *     `originalException`, so the `__sentryIgnore` flag below never reaches `beforeSend`
 *     for it.
 *   - `__sentryIgnore` is read by `beforeSend` in main.tsx and is the backup for the rarer
 *     path where the error escapes uncaught (captureException).
 */
export type ExpectedAuthError = { __sentryIgnore?: boolean; __sessionExpired?: boolean };

/**
 * Mark a genuine session-expiry error so neither Sentry path files a ticket.
 *
 * Set ONLY for failures that end the session and redirect to login. A 401 that survives
 * a fresh-token retry is a real backend/authorizer bug and must keep reaching Sentry —
 * see `gqlClient`.
 */
export function markSessionExpired(err: unknown): void {
  if (err && typeof err === 'object') {
    (err as ExpectedAuthError).__sentryIgnore = true;
    (err as ExpectedAuthError).__sessionExpired = true;
  }
}

/** Mark the error as expected and end the local session (sign-out + redirect to login). */
export function expireSession(err: unknown): void {
  markSessionExpired(err);
  void authService.expireSession();
}

/**
 * The error every authenticated request path raises when Cognito cannot recover the
 * session. Creating it already ends the session, so a caller only has to throw it.
 *
 * Shared by `gqlClient` and the S3 storage helper (WA-3864): before that fix the upload
 * path had no session handling at all, so a refresh token that died between two GraphQL
 * calls surfaced as Amplify Storage's raw `Missing identity ID when accessing objects in
 * protected or private access level.` — an `errors.unknown` snackbar plus a Sentry ticket
 * for what is really a logout.
 */
export function createSessionExpiredError(): Error & ExpectedAuthError {
  const err = new Error('Session expired') as Error & ExpectedAuthError;
  expireSession(err);
  return err;
}

/**
 * In-flight Cognito refresh deduplication.
 *
 * When the user opens a page that fires N parallel dictionary queries on mount (a typical
 * create-order screen does ~5–10), or uploads a batch of payment files in one step, an
 * expired idToken would otherwise trigger N independent forced token reads, hammering
 * Cognito and risking rate-limit throttles. Holding a single Promise that all callers await
 * collapses those into one network round-trip; once it resolves the slot is cleared so the
 * next legitimate refresh can run on its own.
 *
 * Since WA-3947 `auth.service` collapses concurrent reads of the same kind itself (one
 * in-flight slot per plain / forced read); this helper still owns the 15 s ceiling and the
 * Promise every caller awaits.
 *
 * Resolves to `''` when the refresh token itself is dead — the caller then ends the session
 * with `createSessionExpiredError()`.
 */
let refreshInFlight: Promise<string> | null = null;
export function refreshIdTokenOnce(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = firstValueFrom(
    authService.sub.refreshIdToken().pipe(take(1), timeout({ first: REFRESH_ID_TOKEN_TIMEOUT_MS })),
  ).finally(() => {
    // Clear on a microtask so any callers that started reading immediately after the
    // awaited refresh land on a fresh slot — not on the same resolved Promise that's no
    // longer relevant.
    queueMicrotask(() => {
      refreshInFlight = null;
    });
  });
  return refreshInFlight;
}
